import Swup from 'swup';
import SwupHeadPlugin from '@swup/head-plugin';
import SwupPreloadPlugin from '@swup/preload-plugin';
import SwupScrollPlugin from '@swup/scroll-plugin';
import SwupA11yPlugin from '@swup/a11y-plugin';
import SwupJsPlugin from '@swup/js-plugin';
import SwupFormsPlugin from '@swup/forms-plugin';
import { initHistory, trackDirection } from './history.js';
import { resolveTransition } from './transitions.js';
import { initHomeDesignFilters } from './home-design-filters.js';

// Module-scoped handle on the running router, captured in createRouter().
// The ONLY thing outside this file that ever holds the Swup instance — every
// other module navigates through navigateTo() below, never the raw instance,
// per the "wrap third-party APIs" invariant.
let _swup = null;

/**
 * Is this visit a filter / query-only change — same path, different query string?
 *
 * The listing filter bar, active-filter pill removals and "clear all" all land
 * here. Exported so the filter-progress module tests the same condition rather
 * than depending on hook registration order to see visit.isQueryOnly.
 *
 * @param {Object} visit - Swup visit object
 * @returns {boolean}
 */
export function isFilterVisit(visit) {
    const from = visit?.from?.url;
    const to = visit?.to?.url;
    if (from == null || to == null) return false;
    if (visit.to.hash && from === to) return false;

    return from.split('?')[0] === to.split('?')[0];
}

/**
 * Create and configure the Swup router instance.
 *
 * Registers all plugins, wires history direction tracking,
 * transition resolution, data-transition attribute updates,
 * and mobile nav close hook (safe no-op until Epic 4).
 *
 * @returns {Swup} Configured Swup instance
 */
export function createRouter() {
    // Stamp initial history entry with psdIndex
    initHistory();

    const swup = new Swup({
        containers: ['#swup'],
        native: true,
        cache: true,
        // Pure hash links (the detail-page anchor nav, any in-page #links)
        // never enter swup: routed through swup they jump instantly — the
        // native view transition freezes the document around the scroll,
        // and the scroll plugin's spring dies when async-Alpine islands
        // mount mid-flight. Left to the browser, the jump uses CSS smooth
        // scrolling (base/_reset.css), honours the targets'
        // scroll-margin-top, and re-targets through layout shifts.
        // [data-no-swup] is swup's default ignore rule, preserved here.
        ignoreVisit: (url, { el } = {}) =>
            el?.getAttribute('href')?.startsWith('#') || el?.closest('[data-no-swup]') != null,
        plugins: [
            // persistTags keeps Vite's runtime-injected stylesheets in <head>.
            // The floorplan configurator is a dynamic import, so its code-split
            // CSS chunk is appended to <head> by Vite's preload helper on first
            // mount — it is never part of a server-rendered head, so the head
            // plugin sees it as orphaned and strips it on the next visit. Vite's
            // helper caches every dep it has injected, so the second mount
            // short-circuits and never re-adds the link: the configurator
            // renders unstyled until a hard reload. Persisting them is safe —
            // partials/_head ships exactly one stylesheet (app.css) and no
            // inline <style>, so no page-specific CSS can leak across visits.
            // The bare `style` arm covers every runtime injector, not just
            // Vite's dev-mode SFC styles (which was all the old
            // style[data-vite-dev-id] selector matched). Google Maps hits the
            // identical bug: loading the Maps JS appends 5 <style> tags to
            // <head> — including `.gm-style img { max-width: none }`, which is
            // what holds our reset's `img { max-width: 100% }` off Google's
            // control icons. They carry NO attributes, so the old selector
            // never matched them and the head plugin stripped all 5 on the way
            // out of the packages page. Coming back, the Maps script is already
            // loaded and never re-injects, so the map rebuilt with no styles:
            // blown-out fullscreen icon, zoom control clipped at the map edge,
            // doubled attribution. A hard reload fixed it, which is exactly the
            // signature of head state lost across a visit. MOHO-122 follow-up.
            //
            // Persisting ALL <style> is safe for the same reason the link arm
            // is: partials/_head ships one stylesheet (app.css) and no inline
            // <style>, verified against the served HTML — so every <style> in
            // the head at runtime was injected by a script, and none of it is
            // page-specific CSS that could leak across visits.
            new SwupHeadPlugin({
                persistTags: 'link[rel=stylesheet], style',
            }),
            new SwupPreloadPlugin(),
            // animateScroll off — between-page resets are instant jumps
            // (html.is-rendering also pins CSS scroll-behavior to auto
            // during content swaps). Same-page anchors never reach this
            // plugin (see ignoreVisit above).
            new SwupScrollPlugin({ animateScroll: false }),
            new SwupA11yPlugin(),
            // Opt-in (default selector form[data-swup-form]) so only the
            // listing filter form is Swup-handled — the POST enquiry forms are
            // untouched. GET filter submits become Swup visits → #swup swaps,
            // grid + active-state re-render server-side, no full reload.
            new SwupFormsPlugin(),
            new SwupJsPlugin([
                {
                    from: '(.*)',
                    to: '(.*)',
                    out: (done) => done(),
                    in: (done) => done(),
                },
            ]),
        ],
    });

    // Track direction and resolve transition on every visit
    swup.hooks.on('visit:start', (visit) => {
        // Same-page anchor visits (anchor nav, in-page hash links) skip the
        // view transition entirely: inside document.startViewTransition the
        // document is frozen, so the scroll plugin's jump would land
        // instantly — bypassing the CSS smooth scrolling that animates it.
        if (visit.to.hash && visit.from.url === visit.to.url) {
            visit.animation.animate = false;
            return;
        }

        // Filter / query-only visits — same path, only the query string differs:
        // the listing filter bar, active-filter pill removals, "clear all". With
        // native view transitions the browser snapshots and crossfades the WHOLE
        // viewport (the persistent nav included), which reads as a full page
        // reload. Skip the animation + scroll reset so only the results + pills
        // change, in place. Same path is the signal; tab switches that change the
        // path (/narrow-homes, /home-designs/4-bedroom) keep the normal transition.
        if (isFilterVisit(visit)) {
            visit.animation.animate = false;
            // Flag for the listing-grid cascade (app.js): the native page
            // transition is off, so the result cards cascade themselves in
            // instead of the whole viewport crossfading. The matching "leaving"
            // state is the CSS grid dim (router/filter-progress.js).
            visit.isQueryOnly = true;
            if (visit.scroll) {
                visit.scroll.reset = false;
            }
            return;
        }

        // Resolve transition name from registry (sets visit.animation.name)
        resolveTransition(visit);

        // Track direction and append --reverse for backward nav
        trackDirection(visit);

        // Update body data-transition attribute for CSS scoping
        document.body.dataset.transition = visit.animation.name;
    });

    // Close mobile nav on navigation (safe no-op until Epic 4 adds the store)
    swup.hooks.on('visit:start', () => {
        if (window.Alpine?.store('nav')) {
            window.Alpine.store('nav').mobileOpen = false;
        }
    });

    // Keep body[data-namespace] in sync with the page Swup just rendered. The
    // <body> lives outside #swup, so its server-rendered namespace would stay
    // frozen on the first page — making content_group (analytics), the shader
    // bridge and the transition registry read the wrong page after every SPA
    // navigation. The incoming document is parsed by content:replace time.
    swup.hooks.on('content:replace', (visit) => {
        const namespace = visit?.to?.document?.body?.getAttribute('data-namespace');
        if (namespace != null) {
            document.body.dataset.namespace = namespace;
        }
    });

    initHomeDesignFilters(swup);

    _swup = swup;

    return swup;
}

/**
 * Navigate to a URL via Swup, without a full page reload — the wrapped
 * programmatic-navigation API for modules that don't hold the router instance
 * (e.g. the enquiry-form submit handler routing to a thank-you page). Falls
 * back to a real navigation if called before the router has booted, so callers
 * are never left stuck (server-first invariant).
 *
 * @param {string} url
 * @param {import('swup').NavigationOptions} [options]
 */
export function navigateTo(url, options = {}) {
    if (!url) return;
    if (_swup) {
        _swup.navigate(url, options);
    } else {
        window.location.assign(url);
    }
}
